import { lazyRetry } from './utils/lazyRetry';  // CON-1606 — survive flaky mobile chunk loads
import React, { useEffect, useState } from 'react';
import { Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
// EV-54: analytics.ts (consent-gated) is mounted via AnalyticsTracker below.
import { AnalyticsTracker } from './lib/useAnalytics';  // EV-54 — null-render route-change tracker
import { motion, AnimatePresence } from 'framer-motion';
import { Toaster, toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import ProtectedRoute from './components/Auth/ProtectedRoute';
import LoginForm from './components/Auth/LoginForm';
// import LandingPage from './components/Common/LandingPage'; // ⚠️ LEGACY: Removed from routing, kept for reference
import VenueMap from './components/Map/VenueMap';
import VenueMapLegacy from './components/Map/VenueMapLegacy';
import VenueDetails from './components/Venue/VenueDetails';
import SpeedTestPage from './components/SpeedTest/SpeedTestPage';
import ErrorBoundary from './components/Common/ErrorBoundary';
import { useAuth } from './hooks/useAuth';
import DiscoveryPage from './components/Discovery/DiscoveryPage';
import AllEventsPage from './components/Events/AllEventsPage';
import EventsMapPage from './pages/EventsMapPage';
import InstallPrompt from './components/Common/InstallPrompt';
import ProximityWatcher from './components/Common/ProximityWatcher';  // EV-139 — live-event proximity prompt
import { autoBuildOfflinePackIfNeeded } from './utils/offlinePack';
import { captureRefIfPresent, markRefConverted } from './utils/shareLink';
import ContactUsPage from './components/Common/ContactUsPage';
import { sendLoginNotification } from './services/loginNotificationService';
import { detectRegion } from './config/region';
import { useUITranslations } from './hooks/useUITranslations';
import AuthCallback from './components/Auth/AuthCallback';
import { useUserStore } from './stores/useUserStore';
import ProfileCompletionScreen from './pages/ProfileCompletionScreen';
import JoinVenuePage from './pages/JoinVenuePage';
import ZaloAuthCompletePage from './pages/ZaloAuthCompletePage';
import { useUIStore } from './stores/useUIStore';

// V2 consumer frontend (new Vite/TypeScript rebuild)
const MapPageV2 = lazyRetry(() => import('./app-v2/MapPage'), 'MapPage');

// EV-691 — sticky bottom nav on EVERY page. The map ('/') and the /me opener
// render MapPage's own in-column BottomNav; standalone sub-pages (/me/*, /pay,
// /menu, purchases…) get this GLOBAL fixed nav + a global Me overlay so the 4
// tabs are reachable everywhere (tap Events → straight to the map, no back-spam).
import BottomNav from './components/navigation-v2/BottomNav';
const MeScreenGlobal = lazyRetry(() => import('./app-v2/MeScreen'), 'MeScreen');
function GlobalBottomNav() {
  const { pathname } = useLocation();
  const myReviewsOpen = useUIStore((s) => s.myReviewsOpen);
  const setMyReviewsOpen = useUIStore((s) => s.setMyReviewsOpen);
  // Auth flows have no nav.
  if (/^\/(login|logout|complete-profile|auth)/.test(pathname)) return null;
  // '/' renders MapPage with its own in-column nav (no overlay covering it) — leave it.
  if (pathname === '/') return null;
  // /pay-vn is a clean standalone payments page — no footer/bottom nav.
  if (pathname === '/pay-vn') return null;
  // EV-691 — the nav must appear EVERYWHERE, incl. the Me page. On '/me' MapPage is
  // mounted and renders its own MeScreen overlay (zIndex 200), so we DON'T render a
  // second MeScreen here (would double up) — but we DO render the nav above the
  // overlay (zIndex 210 > 200) so it shows on top of the Me page. On every other
  // route MapPage isn't mounted, so we open the MeScreen overlay here when the flag
  // is set (so /me/* sub-pages' "Me" tab works).
  const mapMounted = pathname === '/me';
  return (
    <>
      {!mapMounted && myReviewsOpen && (
        <React.Suspense fallback={null}>
          <MeScreenGlobal onClose={() => setMyReviewsOpen(false)} />
        </React.Suspense>
      )}
      <div style={{ position: 'fixed', left: 0, right: 0, bottom: 0, zIndex: 210 }}>
        <BottomNav />
      </div>
    </>
  );
}

// EV-665 — a real /me route. The "Me" page is a Zustand-driven overlay on the
// map (useUIStore.myReviewsOpen), not its own route, so navigating to /me used
// to fall through to the map. This opener forces the overlay open on mount so
// every /me/* sub-screen's Back button can return to the Me list reliably.
function MeRoute() {
  const { t } = useTranslation();
  const setMyReviewsOpen = useUIStore((s) => s.setMyReviewsOpen);
  useEffect(() => { setMyReviewsOpen(true); }, [setMyReviewsOpen]);
  return (
    <React.Suspense fallback={<div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: '#fafafa' }}>{t('loading.generic')}</div>}>
      <MapPageV2 />
    </React.Suspense>
  );
}
const VenueDeepLink = lazyRetry(() => import('./app-v2/VenueDeepLink'), 'VenueDeepLink');
const AtVenuePage = lazyRetry(() => import('./app-v2/AtVenuePage'), 'AtVenuePage');
// v68.73: presence-proof gate for hlee.app QR scans (only triggers when ?h=<code> present).
const GeofenceGate = lazyRetry(() => import('./components/GeofenceGate'), 'GeofenceGate');
const CookieConsent = lazyRetry(() => import('./components/Common/CookieConsent'), 'CookieConsent');
// EV-1245 — "Two accounts, one you?" duplicate-account merge prompt (v69.749)
const MergeAccountsPrompt = lazyRetry(() => import('./components/Auth/MergeAccountsPrompt'), 'MergeAccountsPrompt');
const MenuPage = lazyRetry(() => import('./app-v2/MenuPage'), 'MenuPage');
const EventsFeedPage = lazyRetry(() => import('./app-v2/EventsFeedPage'), 'EventsFeedPage');  // v68.201
const HangoutsFeedPage = lazyRetry(() => import('./app-v2/HangoutsFeedPage'), 'HangoutsFeedPage');  // EV-1448 — social hangouts layer
const MyCalendarPage = lazyRetry(() => import('./app-v2/MyCalendarPage'), 'MyCalendarPage');  // v68.211
const MySavedEventsPage = lazyRetry(() => import('./app-v2/MySavedEventsPage'), 'MySavedEventsPage');  // EV-493 — saved events + recommendations
const MySavedVenuesPage = lazyRetry(() => import('./app-v2/MySavedVenuesPage'), 'MySavedVenuesPage');  // EV-119
const MyPurchasesPage = lazyRetry(() => import('./app-v2/MyPurchasesPage'), 'MyPurchasesPage');  // EV-417 — purchase history
const MyServiceOrdersPage = lazyRetry(() => import('./app-v2/MyServiceOrdersPage'), 'MyServiceOrdersPage');  // EV-435 — laundry/service orders
const MyPointsPage = lazyRetry(() => import('./app-v2/MyPointsPage'), 'MyPointsPage');  // EV-603 P5 — full gamification panel
const MyLeesPage = lazyRetry(() => import('./app-v2/MyLeesPage'), 'MyLeesPage');  // CON-525 — Collect the Lees (stamp card + album + rewards)
const MyLeaderboardPage = lazyRetry(() => import('./app-v2/MyLeaderboardPage'), 'MyLeaderboardPage');  // EV-884 — consumer leaderboard
const MyFavoriteDishesPage = lazyRetry(() => import('./app-v2/MyFavoriteDishesPage'), 'MyFavoriteDishesPage');  // EV-766 — favourite dishes per venue
const CityGalleryPage = lazyRetry(() => import('./app-v2/CityGalleryPage'), 'CityGalleryPage');  // EV-536 — admin-preview explore-by-photo gallery
const MyWifiTestsPage = lazyRetry(() => import('./app-v2/MyWifiTestsPage'), 'MyWifiTestsPage');  // EV-631 — my own wifi speed tests + where taken
const InboxPage = lazyRetry(() => import('./app-v2/InboxPage'), 'InboxPage');  // EV-392 — venue-subscription inbox
const MySubscriptionsPage = lazyRetry(() => import('./app-v2/MySubscriptionsPage'), 'MySubscriptionsPage');  // EV-392 — venues you follow
const OffersToClaimPage = lazyRetry(() => import('./app-v2/OffersToClaimPage'), 'OffersToClaimPage');  // EV-641 — offers I marked "I'm interested" in, with claim countdown
const VenueInviteClaimPage = lazyRetry(() => import('./app-v2/VenueInviteClaimPage'), 'VenueInviteClaimPage');  // EV-437 — venue-role invite claim
const MyEventsPage = lazyRetry(() => import('./pages/MyEventsPage'), 'MyEventsPage');  // v68.421 (EV-10 v1)
const CuratorPlacesPage = lazyRetry(() => import('./pages/CuratorPlacesPage'), 'CuratorPlacesPage');  // EV-588 — curator place-adding
const OrganizerEventEditor = lazyRetry(() => import('./pages/OrganizerEventEditor'), 'OrganizerEventEditor');  // v68.432 (EV-10 v2)
const OrganizerInvitePage = lazyRetry(() => import('./pages/OrganizerInvitePage'), 'OrganizerInvitePage');  // v68.434 (EV-29)
const WifiPage = lazyRetry(() => import('./app-v2/WifiPage'), 'WifiPage');
const Photo360Page = lazyRetry(() => import('./app-v2/Photo360Page'), 'Photo360Page');  // EV-291 — deep link to a specific 360
const PaymentPage = lazyRetry(() => import('./app-v2/PaymentPage'), 'PaymentPage');
const ScanPayPage = lazyRetry(() => import('./app-v2/ScanPayPage'), 'ScanPayPage');  // EV-548 — scan a venue VietQR to log an outside payment
const ScanPaySimPage = lazyRetry(() => import('./app-v2/ScanPaySimPage'), 'ScanPaySimPage');  // clean scan & pay flow
const BirthdayEventPage = lazyRetry(() => import('./app-v2/BirthdayEventPage'), 'BirthdayEventPage');  // EV-751/774 — birthday event + free drink
const DrinkRedeemPage = lazyRetry(() => import('./app-v2/DrinkRedeemPage'), 'DrinkRedeemPage');  // EV-755 — restaurant free-drink redemption
const GiveDrinkPage = lazyRetry(() => import('./app-v2/GiveDrinkPage'), 'GiveDrinkPage');  // EV-755 — scan guest QR → give the free drink

// QR code resolver — tiny component that hits API and redirects
function QRResolver() {
  const { t } = require('react-i18next').useTranslation();
  const { code } = require('react-router-dom').useParams();
  const navigate = require('react-router-dom').useNavigate();
  const [error, setError] = React.useState(null);
  React.useEffect(() => {
    const API = process.env.REACT_APP_BACKEND_URL || 'https://core.honestlee.app';
    fetch(`${API}/api/qr/${code}`).then(r => r.json()).then(data => {
      if (data.success && data.redirect) navigate(data.redirect, { replace: true });
      else setError(t('common.invalidQrCode'));
    }).catch(() => setError(t('common.qrScanFailed')));
  }, [code, navigate, t]);
  if (error) return <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', flexDirection: 'column', gap: 8 }}><div style={{ fontSize: 48 }}>🍽</div><div style={{ fontSize: 16, fontWeight: 600 }}>{error}</div><a href="/" style={{ color: '#E46E34', fontSize: 13 }}>{t('common.openHonestLee')}</a></div>;
  return <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh' }}>{t('common.scanningQrCode')}</div>;
}
const EventDeepLink = lazyRetry(() => import('./app-v2/EventDeepLink'), 'EventDeepLink');
const EventDetailPage = lazyRetry(() => import('./app-v2/EventDetailPage'), 'EventDetailPage');
const OrganizerLandingPage = lazyRetry(() => import('./app-v2/OrganizerLandingPage'), 'OrganizerLandingPage');  // EV-1309
const ActivityDetailPage = lazyRetry(() => import('./app-v2/ActivityDetailPage'), 'ActivityDetailPage');  // EV-708
const ActivitiesFeedPage = lazyRetry(() => import('./app-v2/ActivitiesFeedPage'), 'ActivitiesFeedPage');  // EV-746 — admin-only Activities discovery feed
const OfferClaimPage = lazyRetry(() => import('./app-v2/OfferClaimPage'), 'OfferClaimPage');  // EV-733 — shareable email-locked offer claim
const InvitePage = lazyRetry(() => import('./app-v2/InvitePage'), 'InvitePage');  // EV-751 — personal birthday invite (/i/:code)
const RedeemPage = lazyRetry(() => import('./app-v2/RedeemPage'), 'RedeemPage');  // EV-751 — staff free-drink redeem (/redeem)

// Venue Dashboard (Legacy) — kept for reference, routes moved to /venue-dashboard-legacy/*
import {
  VenueDashboardProvider,
  VenueDashboardPage,
  RedemptionCenterPage,
  OffersManagerPage,
  EventsSchedulingPage,
  StaffManagementPage,
  ReviewsConsolePage,
  WiFiLabPage,
  VitalsPaymentsPage,
  StaffQRPage
} from './components/VenueDashboardLegacy';

// Venue Dashboard V2 — new Neon-backed, role-gated dashboard
import MyVenuesPage from './pages/VenueDashboardV2/MyVenuesPage';
import VenueDashboardHomePage from './pages/VenueDashboardV2/VenueDashboardHomePage';
import VenueDashboardInfoPage from './pages/VenueDashboardV2/VenueDashboardInfoPage';
import VenueDashboardStaffPage from './pages/VenueDashboardV2/VenueDashboardStaffPage';
import VenueDashboardPhotosPage from './pages/VenueDashboardV2/VenueDashboardPhotosPage';
import VenueDashboardWifiPage from './pages/VenueDashboardV2/VenueDashboardWifiPage';
import VenueDashboardMenuPage from './pages/VenueDashboardV2/VenueDashboardMenuPage';
import VenueDashboardOrdersPage from './pages/VenueDashboardV2/VenueDashboardOrdersPage';  // EV-768 — owner order history
import VenueDashboardOffersPage from './pages/VenueDashboardV2/VenueDashboardOffersPage';
import VenueDashboardHangoutsPage from './pages/VenueDashboardV2/VenueDashboardHangoutsPage';  // VEN-1449 — sponsor hangouts
import VenueDashboardLoyaltyPage from './pages/VenueDashboardV2/VenueDashboardLoyaltyPage';  // CON-525 — Collect the Lees (staff inbox + opt-in + stats)
import VenueDashboardEventsPage from './pages/VenueDashboardV2/VenueDashboardEventsPage';
import VenueDashboardReviewsPage from './pages/VenueDashboardV2/VenueDashboardReviewsPage';
import VenueDashboardVitalsPage from './pages/VenueDashboardV2/VenueDashboardVitalsPage';
import VenueDashboardStaffQRPage from './pages/VenueDashboardV2/VenueDashboardStaffQRPage';
import VenueDashboardRedemptionPage from './pages/VenueDashboardV2/VenueDashboardRedemptionPage';
import ServiceOrdersManager from './pages/VenueDashboardV2/ServiceOrdersManager';  // EV-435 — owner/staff laundry orders
import ServicePriceListEditor from './pages/VenueDashboardV2/ServicePriceListEditor';  // EV-435 — service price-list editor
import VenueDashboardPaymentsPage from './pages/VenueDashboardV2/VenueDashboardPaymentsPage';

function App() {
  // v68.344 — hydrate the v2 user store (useUserStore) from /api/users/me on
  // boot when a token exists. The v2 app reads user.avatar_url from this store,
  // but no login path ever populated it (only the token was stored) — so the
  // avatar/name never showed. This is additive (does NOT touch the login flow):
  // any logged-in user gets their profile (incl Google avatar) into the store.
  useEffect(() => {
    try {
      const token = localStorage.getItem('hl_auth_token')
        || localStorage.getItem('venue_app_token')
        || localStorage.getItem('token');
      if (!token) return;
      if (useUserStore.getState().user?.avatar_url) return; // already hydrated
      const base = import.meta.env.VITE_API_URL || 'https://core.honestlee.app';
      fetch(`${base}/api/users/me`, { headers: { Authorization: `Bearer ${token}` } })
        .then((r) => (r.ok ? r.json() : null))
        .then((d) => {
          if (!d || !(d.id || d.userId)) return;
          useUserStore.getState().setUser({
            id: d.id || d.userId,
            userId: d.userId || d.id,
            email: d.email || null,
            name: d.name || null,
            phone: d.phone || null,
            role: d.role || null,
            region: d.region || null,
            avatar_url: d.avatar_url || d.profileImage || null,
          }, token);
        })
        .catch(() => {});
    } catch { /* non-fatal */ }
  }, []);

  // Patrick rule 2026-04-26: auto-build offline pack on first launch (vn region
  // only for now). Skips if already cached within last 24h or on cellular.
  useEffect(() => {
    const region = window.location.hostname.startsWith('vn.') ? 'vn'
                  : window.location.hostname.startsWith('thai.') ? 'th'
                  : 'vn';
    autoBuildOfflinePackIfNeeded(region);
    // Capture ?ref=<token> on first landing → stash in sessionStorage + log
    // the open. Later: when this user signs up, markRefConverted attributes
    // them back to the original sharer for gamification credit.
    captureRefIfPresent();
    // Phase 2 referrals catch-all: every auth path reloads the page after
    // completion. On mount, if a userId is already in localStorage AND a
    // ref token is still in sessionStorage, fire the conversion. Idempotent
    // (clears sessionStorage on success). Catches Google / Zalo / context
    // paths that don't call markRefConverted inline like phone/email OTP do.
    try {
      const uid = localStorage.getItem('user_id');
      if (uid && sessionStorage.getItem('hl_ref_token')) {
        import('./utils/shareLink').then(m => m.markRefConverted(uid, 'signup')).catch(() => {});
      }
    } catch {}
  }, []);

  const { t } = useTranslation();
  const { user, loading } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [oauthProcessing, setOauthProcessing] = useState(false);

  // Load UI string overrides from backend (translator corrections)
  useUITranslations();

  // v68.372/EV-54 — analytics: moved to <AnalyticsTracker /> component (consent-gated).
  // The old useEffect is removed to avoid double-firing; AnalyticsTracker handles it.

  // 🔴 DEPRECATED (Phase C): Old OAuth callback parsing - will be removed in Phase D
  // New GIS token flow doesn't use URL parameters
  /*
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const token = params.get('token');
    const error = params.get('error');
    const errorMessage = params.get('message');
    
    // 🔴 Handle OAuth errors
    if (error && !token && !oauthProcessing) {
      console.error('❌ OAuth Error:', error, errorMessage);
      
      const decodedMessage = errorMessage ? decodeURIComponent(errorMessage) : error;
      toast.error(decodedMessage || 'Authentication failed', {
        duration: 5000,
        icon: '❌'
      });
      
      // Clean URL and redirect after showing error
      setTimeout(() => {
        window.history.replaceState({}, '', location.pathname);
        navigate(user ? '/' : '/landing');
      }, 2000);
      
      return;
    }
    
    // 🟢 Handle successful OAuth callback
    if (token && !oauthProcessing) {
      setOauthProcessing(true);
      console.log('✅✅✅ GOOGLE OAUTH CALLBACK DETECTED ✅✅✅');
      
      const email = params.get('email');
      const name = params.get('name');
      const userId = params.get('userId');
      const phone = params.get('phone');
      const role = params.get('role');
      const loginMethod = params.get('loginMethod');
      const region = params.get('region') || detectRegion();
      const hl_source_token = params.get('hl_source_token');
      const utm_campaign = params.get('utm_campaign');
      const utm_source = params.get('utm_source');
      
      console.log('📧 Email:', email);
      console.log('👤 Name:', name);
      console.log('🆔 User ID:', userId);
      console.log('🌍 Region:', region);
      console.log('🔑 Login Method:', loginMethod);
      
      // ✅ Store auth token (both keys for compatibility)
      localStorage.setItem('venue_app_token', token);
      localStorage.setItem('token', token);
      console.log('✅ Token saved to localStorage');
      
      // ✅ Store user data with both naming conventions
      if (email) {
        localStorage.setItem('user_email', email);
        localStorage.setItem('userEmail', email);
        console.log('✅ Email saved:', email);
      }
      
      if (name) {
        localStorage.setItem('user_name', name);
        localStorage.setItem('userName', name);
        console.log('✅ Name saved:', name);
      }
      
      if (phone) {
        localStorage.setItem('user_phone', phone);
        localStorage.setItem('userPhone', phone);
        console.log('✅ Phone saved:', phone);
      }
      
      if (userId) {
        localStorage.setItem('user_id', userId);
        localStorage.setItem('userId', userId);
        console.log('✅ User ID saved:', userId);
        // Patrick rule 2026-04-26: attribute referrals on signup completion.
        // captureRefIfPresent stashed ref token on App mount; this links
        // the new user back to the original sharer for gamification credit.
        try { markRefConverted(userId, 'signup'); } catch (e) {}
      }
      
      if (role) {
        localStorage.setItem('user_role', role);
        localStorage.setItem('userRole', role);
        console.log('✅ Role saved:', role);
      }
      
      if (region) {
        localStorage.setItem('user_region', region);
        localStorage.setItem('userRegion', region);
        console.log('✅ Region saved:', region);
      }
      
      if (loginMethod) {
        localStorage.setItem('user_login_method', loginMethod);
        localStorage.setItem('loginMethod', loginMethod);
        console.log('✅ Login method saved:', loginMethod);
      }
      
      // ✅ Store tracking data
      if (hl_source_token) {
        localStorage.setItem('user_qr_token', hl_source_token);
        localStorage.setItem('qr_token', hl_source_token);
        console.log('✅ QR token saved:', hl_source_token);
      }
      
      if (utm_campaign) {
        localStorage.setItem('utm_campaign', utm_campaign);
      }
      
      if (utm_source) {
        localStorage.setItem('utm_source', utm_source);
      }
      
      if (utm_campaign || utm_source) {
        const utmData = {
          utm_campaign,
          utm_source,
          timestamp: new Date().toISOString()
        };
        localStorage.setItem('utm_data', JSON.stringify(utmData));
        console.log('✅ UTM data saved:', utmData);
      }
      
      console.log('💾 All user data saved to localStorage');
      
      // ✅ Show success toast
      toast.success(`Welcome back, ${name || 'User'}! 👋`, {
        duration: 3000,
        icon: '✅'
      });
      
      // ✅ Send login notification (non-blocking, optional)
      if (email && name) {
        console.log('📧 Sending login notification...');
        sendLoginNotification({
          email,
          name,
          phone: phone || 'N/A',
          investorType: role || 'CONSUMER',
          loginMethod: loginMethod || 'google',
          region: region || 'ae',
          hl_source_token,
          utm_campaign,
          utm_source,
          timestamp: new Date().toLocaleString()
        }).catch(error => {
          console.warn('⚠️ Login notification failed (non-critical):', error.message);
        });
      }
      
      // Zoho SalesIQ removed 2026-06-10 (EV-488) — first-party analytics (EV-54) replaces it.
      
      // ✅ Clean URL parameters
      window.history.replaceState({}, '', location.pathname);
      console.log('🧹 URL parameters cleaned');
      
      // ✅ Reload page to update auth state
      console.log('🔄 Reloading page in 2 seconds to update auth state...');
      setTimeout(() => {
        window.location.reload();
      }, 2000);
    }
  }, [location.search, location.pathname, navigate, oauthProcessing, user]);
  */
  // ✅ New GIS flow handles auth entirely in LoginForm component

  useEffect(() => {
    if (!loading) {
      document.body.classList.add('app-loaded');
    }
  }, [loading]);

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #fbbf24 0%, #f59e0b 50%, #ea580c 100%)'
      }}>
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
          style={{
            width: '64px',
            height: '64px',
            border: '4px solid rgba(255, 255, 255, 0.3)',
            borderTop: '4px solid white',
            borderRadius: '50%'
          }}
        />
      </div>
    );
  }

  return (
    <ErrorBoundary>
      <div style={{
        minHeight: '100vh',
        backgroundColor: '#fef3c7',
        fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
      }}>
        <InstallPrompt />
        <ProximityWatcher />
        {/* EV-54 — null-render component; fires trackPageview on every route change (consent-gated) */}
        <AnalyticsTracker />

        <AnimatePresence mode="wait" initial={false}>
          <Routes location={location} key={location.pathname}>

            {/* ⚠️ LEGACY: /landing route removed - see .claude/docs/LEGACY.md */}

            <Route
              path="/login"
              element={
                user ? <Navigate to="/" replace /> :
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.3 }}
                >
                  <LoginForm />
                </motion.div>
              }
            />

            {/* V2 is now the default — PUBLIC (no auth required to browse) */}
            <Route
              path="/"
              element={
                <React.Suspense fallback={<div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: '#f3f4f6' }}>{t('loading.generic')}</div>}>
                  <MapPageV2 />
                </React.Suspense>
              }
            />

            {/* V1 map — kept as fallback at /v1 */}
            <Route
              path="/v1"
              element={
                user
                  ? <ProtectedRoute>
                      <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.5 }}
                      >
                        <VenueMap />
                      </motion.div>
                    </ProtectedRoute>
                  : <Navigate to="/login" replace />
              }
            />

            {/* ✅ NEW: Legacy Map Route */}
            <Route
              path="/legacy-map"
              element={
                <ProtectedRoute>
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.5 }}
                  >
                    <VenueMapLegacy />
                  </motion.div>
                </ProtectedRoute>
              }
            />

            {/* QR code resolver — redirects to /at/:slug/:tableId */}
            <Route
              path="/q/:code"
              element={
                <React.Suspense fallback={<div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh' }}>{t('loading.scanner')}</div>}>
                  <QRResolver />
                </React.Suspense>
              }
            />
            {/* v68.201 — chronological events feed (separate from map) */}
            <Route
              path="/events"
              element={
                <React.Suspense fallback={<div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: '#fafafa' }}>{t('loading.events')}</div>}>
                  <EventsFeedPage />
                </React.Suspense>
              }
            />
            {/* EV-1448 — social hangouts: ad-hoc meetups feed + deep-linked detail */}
            <Route
              path="/hangouts"
              element={
                <React.Suspense fallback={<div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: '#fafafa' }}>{t('loading.hangouts')}</div>}>
                  <HangoutsFeedPage />
                </React.Suspense>
              }
            />
            <Route
              path="/hangouts/:id"
              element={
                <React.Suspense fallback={<div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: '#fafafa' }}>{t('loading.hangout')}</div>}>
                  <HangoutsFeedPage />
                </React.Suspense>
              }
            />
            {/* v68.211 — personal calendar of bookmarked events */}
            {/* EV-665 — the Me page itself (opens the overlay); every /me/* Back returns here */}
            <Route path="/me" element={<MeRoute />} />
            <Route
              path="/me/calendar"
              element={
                <React.Suspense fallback={<div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: '#fafafa' }}>{t('loading.yourCalendar')}</div>}>
                  <MyCalendarPage />
                </React.Suspense>
              }
            />
            {/* EV-493 — saved events + recommendations + RSVPs ("For You / Saved") */}
            <Route
              path="/me/saved"
              element={
                <React.Suspense fallback={<div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: '#fafafa' }}>{t('loading.yourSavedEvents')}</div>}>
                  <MySavedEventsPage />
                </React.Suspense>
              }
            />
            {/* EV-119 — saved places + custom lists ("My places") */}
            <Route
              path="/me/places"
              element={
                <React.Suspense fallback={<div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: '#fafafa' }}>{t('loading.yourPlaces')}</div>}>
                  <MySavedVenuesPage />
                </React.Suspense>
              }
            />
            {/* EV-417 — purchase history ("My Purchases") */}
            <Route
              path="/me/purchases"
              element={
                <ProtectedRoute>
                  <React.Suspense fallback={<div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: '#fafafa' }}>{t('loading.yourPurchases')}</div>}>
                    <MyPurchasesPage />
                  </React.Suspense>
                </ProtectedRoute>
              }
            />
            {/* EV-435 — service orders (laundry etc.) */}
            <Route
              path="/me/orders"
              element={
                <ProtectedRoute>
                  <React.Suspense fallback={<div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: '#fafafa' }}>{t('loading.yourOrders')}</div>}>
                    <MyServiceOrdersPage />
                  </React.Suspense>
                </ProtectedRoute>
              }
            />
            {/* EV-603 P5 — full gamification panel (/me/points) */}
            <Route
              path="/me/points"
              element={
                <React.Suspense fallback={<div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: '#fafafa' }}>{t('loading.yourPoints')}</div>}>
                  <MyPointsPage />
                </React.Suspense>
              }
            />
            {/* CON-525 — Collect the Lees (/me/lees) */}
            <Route
              path="/me/lees"
              element={
                <React.Suspense fallback={<div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: '#fafafa' }}>Loading your Lees… 🦊</div>}>
                  <MyLeesPage />
                </React.Suspense>
              }
            />
            {/* EV-884 — consumer leaderboard (/me/leaderboard) */}
            <Route
              path="/me/leaderboard"
              element={
                <React.Suspense fallback={<div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: '#fafafa' }}>{t('loading.leaderboard')}</div>}>
                  <MyLeaderboardPage />
                </React.Suspense>
              }
            />
            {/* EV-766 — favourite dishes (anon device-id OR logged-in; not gated) */}
            <Route
              path="/me/favourite-dishes"
              element={
                <React.Suspense fallback={<div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: '#fafafa' }}>{t('loading.yourDishes')}</div>}>
                  <MyFavoriteDishesPage />
                </React.Suspense>
              }
            />
            {/* EV-536 — city gallery (admin-only preview; the page self-gates by role) */}
            <Route
              path="/me/city-gallery"
              element={
                <React.Suspense fallback={<div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: '#000', color: '#fff' }}>{t('loading.city')}</div>}>
                  <CityGalleryPage />
                </React.Suspense>
              }
            />
            {/* CON-1378 — the PUBLIC City Gallery entry, now wired into the nav on
                mobile/tablet/desktop. The feed + stats APIs went public in v69.900;
                only this route (and the nav tab) were missing, which is why shared
                links like /gallery?photo=… had nowhere to land. */}
            <Route
              path="/gallery"
              element={
                <React.Suspense fallback={<div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: '#000', color: '#fff' }}>{t('loading.city')}</div>}>
                  <CityGalleryPage />
                </React.Suspense>
              }
            />
            {/* v69.864 — city-scoped URL (more cities later) */}
            {/* CON-1479 — HARDWIRED per-city gallery deep links: /gallery/danang,
                /gallery/phuket, /gallery/bangkok, /gallery/kl (page reads the path;
                /gallery/da-nang kept as a legacy alias). */}
            <Route
              path="/gallery/:city"
              element={
                <React.Suspense fallback={<div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: '#000', color: '#fff' }}>{t('loading.city')}</div>}>
                  <CityGalleryPage />
                </React.Suspense>
              }
            />
            {/* EV-631 — my wifi tests (/me/wifi-tests) */}
            <Route
              path="/me/wifi-tests"
              element={
                <React.Suspense fallback={<div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: '#fafafa' }}>{t('loading.yourTests')}</div>}>
                  <MyWifiTestsPage />
                </React.Suspense>
              }
            />
            {/* EV-392 — venues you follow (/me/subscriptions) */}
            <Route
              path="/me/subscriptions"
              element={
                <React.Suspense fallback={<div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: '#fafafa' }}>{t('loading.generic')}</div>}>
                  <MySubscriptionsPage />
                </React.Suspense>
              }
            />
            {/* EV-392 — venue-subscription inbox (/me/inbox) */}
            <Route
              path="/me/inbox"
              element={
                <React.Suspense fallback={<div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: '#fafafa' }}>{t('loading.yourInbox')}</div>}>
                  <InboxPage />
                </React.Suspense>
              }
            />
            {/* EV-641 — offers to claim (/me/offers-to-claim) */}
            <Route
              path="/me/offers-to-claim"
              element={
                <React.Suspense fallback={<div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: '#fafafa' }}>{t('loading.yourOffers')}</div>}>
                  <OffersToClaimPage />
                </React.Suspense>
              }
            />
            {/* v68.421 (EV-10 v1) — organizer "My Events" (gated to logged-in users) */}
            <Route
              path="/me/events"
              element={
                <ProtectedRoute>
                  <React.Suspense fallback={<div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: '#fafafa' }}>{t('loading.yourEvents')}</div>}>
                    <MyEventsPage />
                  </React.Suspense>
                </ProtectedRoute>
              }
            />
            {/* EV-588 — curator "My Curated Places" + add-a-place flow (curator-gated server-side) */}
            <Route
              path="/me/curated"
              element={
                <ProtectedRoute>
                  <React.Suspense fallback={<div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: '#fafafa' }}>{t('loading.generic')}</div>}>
                    <CuratorPlacesPage />
                  </React.Suspense>
                </ProtectedRoute>
              }
            />
            {/* v68.432 (EV-10 v2) — in-PWA organizer event editor (create + edit) */}
            <Route path="/me/events/new" element={
              <ProtectedRoute>
                <React.Suspense fallback={<div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: '#fafafa' }}>{t('loading.generic')}</div>}>
                  <OrganizerEventEditor />
                </React.Suspense>
              </ProtectedRoute>
            } />
            <Route path="/me/events/:id/edit" element={
              <ProtectedRoute>
                <React.Suspense fallback={<div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: '#fafafa' }}>{t('loading.generic')}</div>}>
                  <OrganizerEventEditor />
                </React.Suspense>
              </ProtectedRoute>
            } />
            {/* v68.434 (EV-29) — public organizer invite-claim (handles its own login) */}
            <Route path="/organizer-invite/:token" element={
              <React.Suspense fallback={<div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: '#fafafa' }}>{t('loading.generic')}</div>}>
                <OrganizerInvitePage />
              </React.Suspense>
            } />
            {/* EV-437 — public venue-role invite claim (owner/manager/staff) */}
            <Route path="/venue-invite/:token" element={
              <React.Suspense fallback={<div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: '#fafafa' }}>{t('loading.generic')}</div>}>
                <VenueInviteClaimPage />
              </React.Suspense>
            } />
            {/* Digital Menu page — PUBLIC */}
            <Route
              path="/menu/:slug"
              element={
                <React.Suspense fallback={<div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: '#fff' }}>{t('loading.menu')}</div>}>
                  <MenuPage />
                </React.Suspense>
              }
            />
            {/* 360° deep-link viewer — PUBLIC (EV-291). Opens straight into a
                specific venue 360 fullscreen; share this link anywhere (WhatsApp,
                IG, etc.) → interactive 360 on our site. ?img=<s3Key> picks the photo. */}
            <Route
              path="/360/:slug"
              element={
                <React.Suspense fallback={<div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: '#000', color: '#fff' }}>Loading 360°…</div>}>
                  <Photo360Page />
                </React.Suspense>
              }
            />
            {/* WiFi connection page — PUBLIC */}
            <Route
              path="/wifi/:slug"
              element={
                <React.Suspense fallback={<div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: '#f8fafc' }}>{t('loading.wifi')}</div>}>
                  <WifiPage />
                </React.Suspense>
              }
            />
            {/* EV-548 — Scan a venue's VietQR to pay outside + log it — PUBLIC */}
            <Route
              path="/scan-pay"
              element={
                <React.Suspense fallback={<div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: '#0b1220', color: '#fff' }}>{t('loading.scannerPage')}</div>}>
                  <ScanPayPage />
                </React.Suspense>
              }
            />
            <Route
              path="/pay-vn"
              element={
                <React.Suspense fallback={<div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: '#0b1220', color: '#fff' }}>{t('loading.generic')}</div>}>
                  <ScanPaySimPage />
                </React.Suspense>
              }
            />
            <Route
              path="/b/:offerId"
              element={
                <React.Suspense fallback={<div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: '#0b1220', color: '#fff' }}>{t('loading.generic')}</div>}>
                  <BirthdayEventPage />
                </React.Suspense>
              }
            />
            <Route
              path="/redeem-drinks/:offerId"
              element={
                <React.Suspense fallback={<div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: '#0b1220', color: '#fff' }}>{t('loading.generic')}</div>}>
                  <DrinkRedeemPage />
                </React.Suspense>
              }
            />
            <Route
              path="/give/:claimId"
              element={
                <React.Suspense fallback={<div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: '#0b1220', color: '#fff' }}>{t('loading.generic')}</div>}>
                  <GiveDrinkPage />
                </React.Suspense>
              }
            />
            {/* Payment page — PUBLIC (VietQR) */}
            <Route
              path="/pay/:slug/:tableId?"
              element={
                <React.Suspense fallback={<div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: '#f8fafc' }}>{t('loading.payment')}</div>}>
                  <PaymentPage />
                </React.Suspense>
              }
            />
            {/* "At Venue" QR landing page — PUBLIC. v68.73: wrapped in GeofenceGate
                so hlee.app QR scans (URL includes ?h=<code>) require presence proof
                before content renders. Plain /at/:slug visits (no ?h=) pass through. */}
            <Route
              path="/at/:slug/:tableId?"
              element={
                <React.Suspense fallback={<div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: '#f8fafc' }}>{t('loading.generic')}</div>}>
                  <GeofenceGate>
                    <AtVenuePage />
                  </GeofenceGate>
                </React.Suspense>
              }
            />
            {/* V2 venue deep link — PUBLIC (shareable venue URLs) */}
            <Route
              path="/venue/:slug"
              element={
                <React.Suspense fallback={<div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: '#f3f4f6' }}>{t('loading.venue')}</div>}>
                  <VenueDeepLink />
                </React.Suspense>
              }
            />
            {/* V1 venue detail — kept at original path pattern with venueSlug param */}
            <Route
              path="/venue-v1/:venueSlug"
              element={
                <ProtectedRoute>
                  <motion.div
                    initial={{ opacity: 0, x: 300 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -300 }}
                    transition={{ duration: 0.4, ease: 'easeOut' }}
                  >
                    <VenueDetails />
                  </motion.div>
                </ProtectedRoute>
              }
            />
             {/* /venues/:slug alias — also v2 deep link */}
             {/* /venues/:slug alias — also public */}
             <Route
              path="/venues/:slug"
              element={
                <React.Suspense fallback={<div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: '#f3f4f6' }}>{t('loading.venue')}</div>}>
                  <VenueDeepLink />
                </React.Suspense>
              }
            />

            <Route
              path="/all-events"
              element={
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.4, ease: 'easeOut' }}
                >
                  <AllEventsPage />
                </motion.div>
              }
            />

            {/* V2 events deep link — PUBLIC */}
            <Route
              path="/events"
              element={
                <React.Suspense fallback={<div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: '#f3f4f6' }}>{t('loading.events')}</div>}>
                  <EventDeepLink />
                </React.Suspense>
              }
            />

            {/* Single event detail — PUBLIC. Opened when a user taps an event card (e.g. on a venue's Overview). */}
            <Route
              path="/event/:eventId"
              element={
                <React.Suspense fallback={<div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: '#f3f4f6' }}>{t('loading.event')}</div>}>
                  <EventDetailPage />
                </React.Suspense>
              }
            />
            {/* EV-1309 — PUBLIC organizer landing page (profile + their events, list/map/timeline).
                Reached from the clickable organizer name on event detail + the feed organizer filter. */}
            <Route
              path="/organizer/:slug"
              element={
                <React.Suspense fallback={<div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: '#fafafa' }}>{t('loading.organizer')}</div>}>
                  <OrganizerLandingPage />
                </React.Suspense>
              }
            />
            {/* EV-746 — Activities discovery feed. Route is public, but the ONLY
                entry point (Activities tab) is admin-gated in BottomNav, so it's
                effectively admin-only for now. Global bottom nav shows automatically. */}
            <Route
              path="/activities"
              element={
                <React.Suspense fallback={<div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: '#fafafa' }}>{t('loading.generic')}</div>}>
                  <ActivitiesFeedPage />
                </React.Suspense>
              }
            />
            {/* EV-708 — single activity detail (PUBLIC, evergreen). Global bottom nav shows automatically. */}
            <Route
              path="/activity/:id"
              element={
                <React.Suspense fallback={<div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: '#f3f4f6' }}>{t('loading.generic')}</div>}>
                  <ActivityDetailPage />
                </React.Suspense>
              }
            />
            {/* EV-733 — shareable offer claim landing (email-locked gift offers). Public. */}
            <Route
              path="/offer/:id"
              element={
                <React.Suspense fallback={<div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: '#0b1020', color: '#fff' }}>{t('loading.generic')}</div>}>
                  <OfferClaimPage />
                </React.Suspense>
              }
            />
            {/* EV-751 — personal birthday invite (the code IS the identity). Public. */}
            <Route
              path="/i/:code"
              element={
                <React.Suspense fallback={<div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: '#0b1020', color: '#fff' }}>{t('loading.generic')}</div>}>
                  <InvitePage />
                </React.Suspense>
              }
            />
            {/* EV-751 — staff free-drink redeem dashboard. Public (code-gated). */}
            <Route
              path="/redeem"
              element={
                <React.Suspense fallback={<div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: '#0f172a', color: '#fff' }}>{t('loading.generic')}</div>}>
                  <RedeemPage />
                </React.Suspense>
              }
            />


            <Route
              path="/speed-test"
              element={
                <ProtectedRoute>
                  <motion.div
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 1.1 }}
                    transition={{ duration: 0.4, ease: 'easeInOut' }}
                  >
                    <SpeedTestPage />
                  </motion.div>
                </ProtectedRoute>
              }
            />
            <Route
              path="/venues/:venueSlug/speed-test"
              element={
                <ProtectedRoute>
                  <motion.div
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 1.1 }}
                    transition={{ duration: 0.4, ease: 'easeInOut' }}
                  >
                    <SpeedTestPage />
                  </motion.div>
                </ProtectedRoute>
              }
            />

            <Route
              path="/venue/:venueSlug/speed-test"
              element={
                <ProtectedRoute>
                  <motion.div
                    initial={{ opacity: 0, y: 50 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -50 }}
                    transition={{ duration: 0.4, ease: 'easeOut' }}
                  >
                    <SpeedTestPage />
                  </motion.div>
                </ProtectedRoute>
              }
            />

            <Route
              path="/discovery"
              element={
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.4, ease: 'easeOut' }}
                >
                  <DiscoveryPage />
                </motion.div>
              }
            />

            <Route
              path="/contact"
              element={
                <ProtectedRoute>
                  <motion.div
                    initial={{ opacity: 0, x: 300 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -300 }}
                    transition={{ duration: 0.4, ease: 'easeOut' }}
                  >
                    <ContactUsPage />
                  </motion.div>
                </ProtectedRoute>
              }
            />


            {/* ===== VENUE DASHBOARD V2 — new Neon-backed, role-gated ===== */}
            <Route
              path="/venue-dashboard"
              element={<ProtectedRoute><MyVenuesPage /></ProtectedRoute>}
            />
            <Route
              path="/venue-dashboard/:slug"
              element={<ProtectedRoute><VenueDashboardHomePage /></ProtectedRoute>}
            />
            <Route
              path="/venue-dashboard/:slug/info"
              element={<ProtectedRoute><VenueDashboardInfoPage /></ProtectedRoute>}
            />
            <Route
              path="/venue-dashboard/:slug/staff"
              element={<ProtectedRoute><VenueDashboardStaffPage /></ProtectedRoute>}
            />
            <Route
              path="/venue-dashboard/:slug/photos"
              element={<ProtectedRoute><VenueDashboardPhotosPage /></ProtectedRoute>}
            />
            <Route
              path="/venue-dashboard/:slug/wifi"
              element={<ProtectedRoute><VenueDashboardWifiPage /></ProtectedRoute>}
            />
            <Route
              path="/venue-dashboard/:slug/menu"
              element={<ProtectedRoute><VenueDashboardMenuPage /></ProtectedRoute>}
            />
            <Route
              path="/venue-dashboard/:slug/orders"
              element={<ProtectedRoute><VenueDashboardOrdersPage /></ProtectedRoute>}
            />
            <Route
              path="/venue-dashboard/:slug/offers"
              element={<ProtectedRoute><VenueDashboardOffersPage /></ProtectedRoute>}
            />

            <Route
              path="/venue-dashboard/:slug/hangouts"
              element={<ProtectedRoute><VenueDashboardHangoutsPage /></ProtectedRoute>}
            />
            <Route
              path="/venue-dashboard/:slug/loyalty"
              element={<ProtectedRoute><VenueDashboardLoyaltyPage /></ProtectedRoute>}
            />
            <Route
              path="/venue-dashboard/:slug/events"
              element={<ProtectedRoute><VenueDashboardEventsPage /></ProtectedRoute>}
            />
            <Route
              path="/venue-dashboard/:slug/reviews"
              element={<ProtectedRoute><VenueDashboardReviewsPage /></ProtectedRoute>}
            />
            <Route
              path="/venue-dashboard/:slug/vitals"
              element={<ProtectedRoute><VenueDashboardVitalsPage /></ProtectedRoute>}
            />
            <Route
              path="/venue-dashboard/:slug/staff-qr"
              element={<ProtectedRoute><VenueDashboardStaffQRPage /></ProtectedRoute>}
            />
            {/* EV-435 — laundry/service orders + price-list editor (owner/manager/staff) */}
            <Route
              path="/venue-dashboard/:slug/service-orders"
              element={<ProtectedRoute><ServiceOrdersManager /></ProtectedRoute>}
            />
            <Route
              path="/venue-dashboard/:slug/service-prices"
              element={<ProtectedRoute><ServicePriceListEditor /></ProtectedRoute>}
            />
            <Route
              path="/venue-dashboard/:slug/redemption"
              element={<ProtectedRoute><VenueDashboardRedemptionPage /></ProtectedRoute>}
            />
            <Route
              path="/venue-dashboard/:slug/payments"
              element={<ProtectedRoute><VenueDashboardPaymentsPage /></ProtectedRoute>}
            />

            {/* ===== VENUE DASHBOARD LEGACY — old mock-data version, kept for reference ===== */}
            <Route
              path="/venue-dashboard-legacy"
              element={
                <ProtectedRoute>
                  <VenueDashboardProvider>
                    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.3 }}>
                      <VenueDashboardPage />
                    </motion.div>
                  </VenueDashboardProvider>
                </ProtectedRoute>
              }
            />
            <Route path="/venue-dashboard-legacy/redemption" element={<ProtectedRoute><VenueDashboardProvider><RedemptionCenterPage /></VenueDashboardProvider></ProtectedRoute>} />
            <Route path="/venue-dashboard-legacy/offers" element={<ProtectedRoute><VenueDashboardProvider><OffersManagerPage /></VenueDashboardProvider></ProtectedRoute>} />
            <Route path="/venue-dashboard-legacy/events" element={<ProtectedRoute><VenueDashboardProvider><EventsSchedulingPage /></VenueDashboardProvider></ProtectedRoute>} />
            <Route path="/venue-dashboard-legacy/staff" element={<ProtectedRoute><VenueDashboardProvider><StaffManagementPage /></VenueDashboardProvider></ProtectedRoute>} />
            <Route path="/venue-dashboard-legacy/reviews" element={<ProtectedRoute><VenueDashboardProvider><ReviewsConsolePage /></VenueDashboardProvider></ProtectedRoute>} />
            <Route path="/venue-dashboard-legacy/wifi-lab" element={<ProtectedRoute><VenueDashboardProvider><WiFiLabPage /></VenueDashboardProvider></ProtectedRoute>} />
            <Route path="/venue-dashboard-legacy/vitals" element={<ProtectedRoute><VenueDashboardProvider><VitalsPaymentsPage /></VenueDashboardProvider></ProtectedRoute>} />
            <Route path="/venue-dashboard-legacy/staff-qr" element={<ProtectedRoute><VenueDashboardProvider><StaffQRPage /></VenueDashboardProvider></ProtectedRoute>} />

            {/* ===== AUTH CALLBACK (Google / LINE OAuth redirect) ===== */}
            <Route
              path="/auth/callback"
              element={
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <AuthCallback />
                </motion.div>
              }
            />

            {/* ===== LINE PROFILE COMPLETION ===== */}
            <Route
              path="/complete-profile"
              element={
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.3 }}
                >
                  <ProfileCompletionScreen />
                </motion.div>
              }
            />

            {/* ===== ZALO AUTH COMPLETION (frontend-side profile fetch) ===== */}
            <Route path="/auth/zalo-complete" element={<ZaloAuthCompletePage />} />

            {/* ===== VENUE INVITE / JOIN ===== */}
            <Route
              path="/join"
              element={
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <JoinVenuePage />
                </motion.div>
              }
            />

            {/* /v2 still works as alias for / */}
            <Route
              path="/v2"
              element={<Navigate to="/" replace />}
            />

            {/* Catch-all — redirect to map (not login) */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </AnimatePresence>

        {/* EV-691 — global sticky bottom nav on standalone sub-pages */}
        <GlobalBottomNav />

        <Toaster
          position="top-center"
          toastOptions={{
            duration: 4000,
            style: {
              background: 'rgba(0, 0, 0, 0.8)',
              color: 'white',
              borderRadius: '12px',
              padding: '16px',
              fontSize: '14px',
              fontWeight: '500',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255, 255, 255, 0.1)'
            },
            success: {
              style: {
                background: 'rgba(16, 185, 129, 0.9)',
                border: '1px solid rgba(16, 185, 129, 0.3)'
              }
            },
            error: {
              style: {
                background: 'rgba(239, 68, 68, 0.9)',
                border: '1px solid rgba(239, 68, 68, 0.3)'
              }
            }
          }}
        />

        {/* EV-446/453 — our own i18n cookie banner (replaces Zoho's English-only one) */}
        <React.Suspense fallback={null}><CookieConsent /></React.Suspense>
        {/* EV-1245 — duplicate-account detection prompt (strong/device signals only) */}
        <React.Suspense fallback={null}><MergeAccountsPrompt /></React.Suspense>
      </div>
    </ErrorBoundary>
  );
}

export default App;